import { createBankApi, deleteBankApi, getBanksApi, updateBankApi } from '@app/api/bank.api';
import { BankDataArray, BankItem, BankQueryParams, BankModel } from '@app/interfaces/bank';
import { AnyAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { message } from 'antd';
import { showError } from '@app/utils/message';

export interface BankState {
  data: BankModel[];
  totalRows: number;
  pagination: {
    current: number,
    pageSize: number
  },
  filters: any,
  sorter: any,
  searchTerm: string;
  selectedRecord: BankItem[];
  loading: boolean;
  error: string | null;
}

const initialState: BankState = {
  data: [],
  totalRows: 0,
  pagination: { current: 1, pageSize: 20 },
  filters: {},
  sorter: {},
  searchTerm: "",
  selectedRecord: [],
  loading: false,
  error: null,
};

interface ResponseData {
  data: BankItem;
  message: string;
}

export const getBanks = createAsyncThunk<BankDataArray, BankQueryParams>(
  'bank/getBanks',
  async (params: BankQueryParams, { rejectWithValue }) => {
    try {
      const data = (await getBanksApi(params)) as BankDataArray;
      return data || [];
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

export const createBank = createAsyncThunk<ResponseData, BankModel>(
  'bank/createBank', async (data) => {
  const response = await createBankApi(data);
  return response;
});

export const updateBank = createAsyncThunk<ResponseData, { bankId: number; formData: BankModel }>(
  'bank/updateBank',
  async ({ bankId, formData }) => {
    const response = await updateBankApi(bankId, formData);
    return response;
  },
);

export const deleteBank = createAsyncThunk<{ bankId: number; response: any }, number>(
  'bank/deleteBank',
  async (bankId, { rejectWithValue }) => {
    try {
      const response = await deleteBankApi(bankId);
      return { bankId, response };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

const isRejectedAction = (action: AnyAction) => {
  return action.type.endsWith('rejected');
};

// Slice
export const bankSlice = createSlice({
  name: 'bank',
  initialState,
  reducers: {
    updateTableParams(state, action) {
      const { pagination, filters, sorter, searchTerm } = action.payload;
      state.pagination = pagination;
      state.filters = filters;
      state.sorter = sorter;
      state.searchTerm = searchTerm;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBanks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBanks.fulfilled, (state, action) => {
        state.loading = false;

        const data = action.payload.rows;
        state.data = data.map((item: BankItem) => item.attributes) || [];

        state.totalRows = parseInt(action.payload.total_rows);
      })
      .addCase(getBanks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch products';
      })
      .addCase(createBank.fulfilled, (state, action) => {
        const { data, message: newMessage } = action.payload;
        state.data.unshift(data.attributes);
        message.success(newMessage);
      })
      .addCase(updateBank.fulfilled, (state, action) => {
        const { data, message: newMessage } = action.payload;

        const updatedIndex = state.data.findIndex((item) => Number(item.id) === Number(data.id));
        if (updatedIndex !== -1) {
          state.data[updatedIndex] = data.attributes;
          message.success(newMessage);
        }
      })
      .addCase(deleteBank.fulfilled, (state, action) => {
        const { bankId, response } = action.payload;
        state.data = state.data.filter((product) => Number(product.id) !== bankId);
        message.success(response.message);
      })
      .addMatcher(isRejectedAction, (state, action) => {
        state.loading = false;
        showError(action.payload || action.error.message);
      });
  },
});

export const { updateTableParams } = bankSlice.actions;

export default bankSlice.reducer;
